/**
 * THE WORK CONTRACTS — what an accountant's piece of work looks like over the wire.
 *
 * ONE PRINCIPLE RUNS THROUGH ALL OF IT: the browser renders; it never derives.
 * Every status, tone, label, note, figure and conclusion below is computed on
 * the server from persisted R2–R4 state and sent already decided. A screen
 * that recomputed "is this resolved?" would be a second answer to a question
 * the professional record has already answered once.
 *
 * So there is no field here the client fills in from its own reasoning, and no
 * shape that would let a screen invent a state the database does not hold.
 */
import { z } from "zod";
import { Currency, IsoDate, Uuid } from "./primitives.js";
import { ControlCandidate, Named, RecordKind, RecordRole, Tone } from "./vocabulary.js";
import { Interpretation, Investigation } from "./intent.js";
// ── the state vocabulary ────────────────────────────────────────────────────
/**
 * Six states, and no seventh. There is no governed "Completed" distinct from
 * PREPARED: a Run reaches SUCCEEDED only through a completion evaluator the
 * estate does not have, so splitting one governed state across two labels
 * would invent a distinction the record cannot make.
 */
export const WorkStatus = z.enum([
    "DRAFT", // opened; nothing has been started
    "WORKING", // an attempt is running now
    "WAITING_FOR_EVIDENCE", // the normal, expected state of careful work
    "NEEDS_CLARIFICATION", // a question only a person can answer
    "PREPARED", // conclusions proposed
    "UNSUPPORTED", // Leveri will not perform this work as described
]);
// ── the board ───────────────────────────────────────────────────────────────
export const WorkSummary = z.object({
    id: Uuid,
    title: z.string(), // the objective, in the accountant's own words
    clientId: Uuid,
    clientName: z.string(),
    engagementId: Uuid,
    engagementTitle: z.string(),
    currency: Currency,
    periodStart: IsoDate,
    cutOff: IsoDate,
    status: WorkStatus,
    statusLabel: z.string(),
    tone: Tone,
    openRequestCount: z.number().int().nonnegative(),
    recordCount: z.number().int().nonnegative(),
    workpaperVersion: z.number().int().positive().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /** The period, read for a person: "1 January 2026 to 31 August 2026". */
    periodText: z.string(),
    /** 0034: the member who holds this work, when someone does. */
    assignee: z.object({ memberId: Uuid, userId: Uuid, displayName: z.string() }).nullable().default(null),
    /**
     * Who started the work and when, for a person: "Started by Ada Obi on 12 October 2026". Private asks, shared work
     * (owner ruling 2026-10-12): a person's conversation with Leveri is theirs, but the work it opened is the team's,
     * and the file names who started it. The instruction itself is the title.
     */
    startedText: z.string().nullable().default(null),
});
/** Four groups. An empty group is dropped — a board of empty rails is noise. */
export const WorkGroup = z.object({
    key: z.enum(["NEEDS_ATTENTION", "IN_PROGRESS", "WAITING", "PREPARED"]),
    label: z.string(),
    hint: z.string(),
    work: z.array(WorkSummary),
});
export const WorkBoardResponse = z.object({ groups: z.array(WorkGroup), total: z.number().int().nonnegative() });
// ── the file ────────────────────────────────────────────────────────────────
/** The one thing happening now, stated. */
export const CurrentBand = z.object({ tone: Tone, label: z.string(), note: z.string() });
/**
 * THE STATE LEDGER — the professional position, broken out, because one marker
 * cannot carry it. A difference fully explained on work whose treatment is
 * still unsupported is genuinely both resolved and unfinished.
 */
/**
 * The rows are NAMED, not free-form: the acceptance tests and the screens both
 * address them by key, and a renamed row would silently disappear from a
 * screen rather than fail. `overall` always comes first and subordinates the
 * rest — it can never read as settled while any part below it is open.
 */
export const StateLedgerKey = z.enum(["overall", "cause", "treatment", "workpaper", "open", "review"]);
export const StateLedgerRow = z.object({
    key: StateLedgerKey,
    term: z.string(),
    value: z.string(),
    tone: Tone,
});
/** A ledger line. The text is formatted on the server; the browser never re-formats a figure. */
/** Named for the same reason as the ledger rows. `difference` is control minus detail. */
export const FigureKey = z.enum(["control", "detail", "difference"]);
/**
 * R8.1 — HOW A FIGURE STANDS AGAINST THE ENGAGEMENT'S MATERIALITY, decided on
 * the server: the band, and the sentence a senior would write ("NGN
 * 525,000.00 · 0.20% of the control balance · above clearly trivial"). Null
 * when no materiality is set. Presentation only: it never changes a conclusion.
 */
export const MaterialityAssessment = z.object({
    band: z.enum(["CLEARLY_TRIVIAL", "BELOW_PERFORMANCE", "BELOW_OVERALL", "MATERIAL"]),
    label: z.string(),
    sentence: z.string(),
    tone: Tone,
});
/**
 * R8.2 — THE RECONCILIATION AS AN ACCOUNTANT LAYS IT OUT: from one side,
 * through each reconciling item with its reason and the rows it rests on, to
 * the other side — and whether it ties. Built on the server from the version's
 * own content; anything the version did not explain is an explicit
 * "Unexplained difference" line, never absorbed. Items rank by amount.
 */
export const BridgeCitation = z.object({
    filename: z.string().nullable(),
    sha256: z.string().nullable(),
    sheetName: z.string(),
    rowIndex: z.number().int().nonnegative(),
});
export const BridgeItem = z.object({
    direction: z.enum(["ADD", "LESS"]),
    label: z.string(),
    reason: z.string(),
    minorUnits: z.number().int(),
    text: z.string(),
    explained: z.boolean(),
    citations: z.array(BridgeCitation),
    assessment: MaterialityAssessment.nullable(),
    /** As printed — decided on the server: "Add: EXP000002", "(NGN 525,000.00)", "file › sheet row 47". */
    lineLabel: z.string(),
    valueText: z.string(),
    citationText: z.string().nullable(),
});
/** One end of the statement: a balance, and — where the content states it — the row it was read from. */
export const BridgeEnd = z.object({
    label: z.string(), minorUnits: z.number().int(), text: z.string(),
    citation: BridgeCitation.nullable(),
    citationText: z.string().nullable(),
});
export const Bridge = z.object({
    from: BridgeEnd,
    items: z.array(BridgeItem),
    to: BridgeEnd,
    ties: z.boolean(),
    sentence: z.string(),
    /** How "Add" and "Less" read on this statement — stated, never left to the reader. Null where the capability states none. */
    convention: z.string().nullable(),
});
export const FigureRow = z.object({
    key: FigureKey,
    label: z.string(),
    citation: z.string().nullable(),
    minorUnits: z.number().int(),
    text: z.string(),
    total: z.boolean(),
    assessment: MaterialityAssessment.nullable().default(null),
});
/**
 * A Fact, as the record states it — never re-worded.
 *
 * `statement` is the engine's own text, verbatim, and it is what the workpaper
 * files. `reading` is that same text made readable for a screen: the engine
 * writes "NGN 266081688.00" and a person reads "NGN 266,081,688.00". The
 * humanising happens on the server so that the stored sentence and the
 * displayed one can never drift apart in a browser, and null means the two are
 * the same.
 */
export const EstablishedItem = z.object({
    id: Uuid,
    factKind: z.string(),
    /** What kind of thing this is, in words. The enum stays for provenance. */
    factKindLabel: z.string().default(""),
    statement: z.string(),
    reading: z.string().nullable().default(null),
    /** The accountant's phrasebook sentence for the screen (work-os/phrasebook.ts). Null: no phrase; show `reading`. Never used by the workpaper. */
    plain: z.string().nullable().default(null),
    derivation: z.string(),
    /**
     * HOW IT WAS ESTABLISHED, in words — "Computed by Leveri from the records"
     * rather than DETERMINISTIC_COMPUTATION. The screen showed the enum under
     * every fact; an accountant is not required to learn it.
     */
    derivationLabel: z.string().default(""),
    basisCount: z.number().int().nonnegative(),
    /**
     * Whether the CURRENT workpaper version cites this. A superseded fact is
     * professional history, not the current reading, and the two must never sit
     * beside each other as though both were now.
     */
    current: z.boolean().default(true),
});
/**
 * R7 (ADR-0014) — A PERSON'S REVIEW ACT on one conclusion, as the record holds
 * it: what they did (accept, adjust, reject, request evidence), who, in what
 * role, under which authority, when, on what basis, against which workpaper
 * version. Never the engine's: the Decision itself stays PROPOSED.
 */
export const ReviewOutcome = z.enum(["ACCEPTED", "ADJUSTED", "REJECTED", "EVIDENCE_REQUESTED"]);
export const ReviewAct = z.object({
    outcome: ReviewOutcome,
    outcomeLabel: z.string(),
    reviewer: z.string(),
    roleLabel: z.string(),
    /** Why this person was permitted: the authority they held, in words. */
    authorityLabel: z.string(),
    reviewedAt: z.string(),
    reviewedAtText: z.string(),
    /** "Basis", "Reason" or "Evidence needed" — what the reviewer's words are. */
    basisLabel: z.string(),
    basis: z.string(),
    /** The reviewer's own conclusion, when they adjusted Leveri's. */
    adjustedConclusion: z.object({ conclusionKind: z.string(), label: z.string(), text: z.string() }).nullable(),
    /** ADR-0019: what was off, when the reviewer said (a learning label; never on the workpaper). */
    correctionClass: z.enum(["WRONG", "NOISE", "WORDING"]).nullable().default(null),
    correctionLabel: z.string().nullable().default(null),
    versionSeq: z.number().int().positive(),
});
/** Why a review act no longer covers the current workpaper. Stated, never silent. */
export const ReviewSupersession = z.object({
    versionSeq: z.number().int().positive(),
    reasonKind: z.enum(["NEW_EVIDENCE", "REPERFORMED"]),
    reason: z.string(),
    supersededAt: z.string(),
    supersededAtText: z.string(),
});
export const ReviewStatus = z.enum([
    "PROPOSED", "ACCEPTED", "ADJUSTED", "REJECTED", "EVIDENCE_REQUESTED", "RECONSIDERATION_REQUIRED", "REVIEW_SUPERSEDED",
]);
/** A Decision, with its question and conclusion exactly as committed. */
export const ConclusionRow = z.object({
    id: Uuid,
    questionKind: z.string(),
    questionText: z.string(),
    conclusionKind: z.string(),
    conclusionText: z.string(),
    /** The same sentence, read for a person. Null when it needed no reading. */
    conclusionReading: z.string().nullable().default(null),
    /** The accountant's phrasebook sentence for the screen. Null: no phrase; show `conclusionReading`. Never used by the workpaper. */
    conclusionPlain: z.string().nullable().default(null),
    /**
     * PROPOSED — what the ENGINE recorded, always. A person's review never
     * changes this value (ADR-0014 law 1); it is carried beside it in `review`.
     */
    governanceStatus: z.string(),
    /** The same status in words: "Proposed", never the bare token. */
    governanceLabel: z.string().default(""),
    /** The question in words, so the two professional acts can be named apart. */
    questionLabel: z.string().default(""),
    /** False once a later workpaper version supersedes it. History, not now. */
    current: z.boolean().default(true),
    tone: Tone,
    /**
     * R7 — THE REVIEW POSITION of this conclusion. PROPOSED until a person with
     * review authority acts; then that act. A current conclusion whose
     * predecessor had been reviewed reads RECONSIDERATION_REQUIRED, carrying the
     * earlier act in `previous`; a history row whose act a later version
     * replaced reads REVIEW_SUPERSEDED, carrying the reason.
     */
    review: z.object({
        status: ReviewStatus,
        label: z.string(),
        tone: Tone,
        act: ReviewAct.nullable(),
        supersession: ReviewSupersession.nullable(),
        previous: ReviewAct.extend({ supersession: ReviewSupersession }).nullable(),
    }).default({ status: "PROPOSED", label: "Proposed", tone: "neutral", act: null, supersession: null, previous: null }),
    /** Whether THIS viewer may review this conclusion now. Decided on the server. */
    canReview: z.boolean().default(false),
    /** The other conclusions the question allows, named — for an adjustment. */
    adjustOptions: z.array(z.object({ conclusionKind: z.string(), label: z.string() })).default([]),
});
/**
 * WHAT EXPLAINS THE DIFFERENCE, as the engine established it — and the
 * transaction named as the client's own record names it.
 *
 * The engine cites a LOCATION (sheet and row), not a document number, because
 * the number is the client's and belongs to their export rather than to our
 * conclusion. The reference below is read back out of that very row by the
 * capability's own rule, so a screen shows the transaction without anyone
 * having copied an identifier into a conclusion where it could go stale.
 */
export const Explanation = z.object({
    reference: z.string().nullable(),
    referenceColumn: z.string().nullable(),
    locations: z.array(z.object({ sheetName: z.string(), rowIndex: z.number().int().nonnegative() })),
    mechanisms: z.array(z.string()),
    /** The same mechanisms in words. MISSING_LINKAGE_KEY is not a sentence. */
    mechanismLabels: z.array(z.string()).default([]),
    mechanismEstablished: z.boolean(),
    minorUnits: z.number().int(),
    text: z.string(),
    /**
     * THE ONE LINE AN ACCOUNTANT READS FIRST: the transaction, the amount and
     * what it was about the posting that put it outside the population.
     * Composed on the server from the engine's own findings.
     */
    headline: z.string().default(""),
});
/**
 * WHETHER THE RECORDS CAN BE COMPARED AT ALL — the engine's own dimensions,
 * read for a person.
 *
 * It matters most when it FAILS. "dated population \"Ledger\" spans
 * 2026-01-01..2026-08-31, outside the declared period 2026-01-01..2026-08-30"
 * is the engine's grounds and belongs in the record; what an accountant needs
 * is what to do about it, which is what `action` carries.
 */
export const ComparabilityRow = z.object({
    dimension: z.string(),
    label: z.string(),
    state: z.string(),
    stateLabel: z.string(),
    tone: Tone,
    /** The engine's own grounds, verbatim. Shown under a disclosure. */
    grounds: z.string(),
    /** What to do about it, when it is not established. Null when nothing is wrong. */
    action: z.string().nullable(),
});
/**
 * What the work is waiting for. The heading is the QUESTION, not the action:
 * an accountant is being asked something, not given a task.
 */
export const OpenRequest = z.object({
    requirementId: Uuid,
    clarificationId: Uuid.nullable(),
    heading: z.string(),
    context: z.string().nullable(),
    question: z.string(),
    /** The question in the accountant's words (the phrasebook); screen only — the workpaper prints `question`. */
    questionPlain: z.string().nullable().default(null),
    detail: z.string().nullable(),
    status: z.string(),
    raisedAt: z.string(),
    raisedAtText: z.string(),
    /**
     * Whether this request may be answered "I don't have this": a person's written statement that the record does
     * not exist, filed in their name, closes it and the work continues with the gap as a limitation. Offered only where
     * the work's procedure carries on without it (account preparation); elsewhere the work would simply ask again.
     */
    mayDeclareNotAvailable: z.boolean().default(false),
    /**
     * R10.2 — what the client sent for THIS request through a link: their
     * response, its files, and what Leveri proposes. Candidate support only;
     * nothing here answers the request until a person confirms it.
     */
    clientResponses: z.array(z.object({
        responseId: Uuid,
        fromText: z.string(),
        receivedAtText: z.string(),
        files: z.array(z.object({ filename: z.string(), evidenceReferenceId: Uuid })),
        proposalText: z.string(),
    })),
});
/** One enclosed record — the act, and what it turned out to be. */
export const EnclosedRecord = z.object({
    enclosureId: Uuid,
    filename: z.string(),
    byteSize: z.number().int().nonnegative(),
    enclosedAt: z.string(),
    enclosedAtText: z.string(),
    enclosedBy: z.string(),
    /** REPLACED: admitted, but the work holds a newer reading of the same file and uses that one (0046). */
    outcome: z.enum(["ADMITTED", "REFUSED", "REPLACED"]),
    refusalReason: z.string().nullable(),
    /** The refusal in plain words (the phrasebook); screen only — the record keeps `refusalReason` verbatim. */
    refusalPlain: z.string().nullable().default(null),
    /** For a REPLACED record: what read the file again, what it found, and which reading the work uses. */
    replacedNote: z.string().nullable().default(null),
    role: RecordRole.nullable(),
    roleLabel: z.string().nullable(),
    sourceVersionId: Uuid.nullable(),
    evidenceReferenceId: Uuid.nullable(),
    sha256: z.string().nullable(),
    reader: z.string().nullable(),
    normalizer: z.string().nullable(),
    sheets: z.array(z.object({ name: z.string(), rowCount: z.number().int().nonnegative() })),
});
/** A governed artifact version. V1 is never rewritten by V2. */
export const WorkpaperVersion = z.object({
    artifactId: Uuid,
    artifactVersionId: Uuid,
    versionSeq: z.number().int().positive(),
    contentHash: z.string(),
    calculationRef: z.string().nullable(),
    contentContractRef: z.string().nullable(),
    createdAt: z.string(),
    createdAtText: z.string(),
    current: z.boolean(),
});
/** Governed outcomes only. Nothing here is a live event. */
export const HistoryEntry = z.object({
    at: z.string(),
    atText: z.string(),
    kind: z.string(),
    statement: z.string(),
});
/**
 * THE CONTROL ACCOUNT — the one genuinely human decision in this capability,
 * and even it is a pick from records already in hand rather than free text.
 * The server proposes from the admitted trial balance; the accountant confirms.
 */
/**
 * WHY THIS LINE IS PROPOSED, when it is — in the accountant's terms, so a
 * proposal can be judged rather than merely accepted. Null when nothing was
 * proposed, which is the honest answer far more often than a guess.
 */
/**
 * THE ROW ITSELF, as the client's own record writes it — every non-empty
 * cell, in order, already read for a person. A control account is confirmed
 * by looking at the line, not by trusting a label, so the line travels.
 */
/**
 * A VALUE AND ITS WORD. Any closed vocabulary a screen has to render arrives
 * like this, because the alternative is a second copy of the words in the
 * browser — which is how ANNUAL_ACCOUNTS ends up printed at an accountant.
 */
/**
 * THE KINDS OF RECORD, with their words.
 *
 * A screen has to name a kind of record BEFORE one is enclosed — in the
 * control that asks for it, and in the list of what is still missing — so
 * there is no `roleLabel` to read from an existing record. Without this the
 * browser would have to keep its own copy of the words, which is how
 * TRIAL_BALANCE ends up printed at an accountant.
 */
/** Why the work cannot be started yet, in the accountant's terms. */
export const StartReadiness = z.object({
    canStart: z.boolean(),
    /** Whether the work has been started: a started work's "blockers" say so, and nothing about it is still needed to start. */
    started: z.boolean().default(false),
    blockers: z.array(z.string()),
    /** The kinds still needed — named, never bare enums. */
    missing: z.array(RecordKind),
    /** Every kind a record can be, for the control that asks. */
    kinds: z.array(RecordKind),
    candidates: z.array(ControlCandidate),
    /** The words around the choice the work starts on — the work's own (a control line, or a bank account). */
    chooser: z.object({ heading: z.string(), empty: z.string(), proposed: z.string(), none: z.string(), chooseFirst: z.string() }).default({
        heading: "The control account",
        empty: "Leveri reads the control lines out of the trial balance itself. There are none to choose from until one is enclosed.",
        proposed: "These lines come from the record you enclosed. Leveri has proposed one; confirm it or pick another.",
        none: "These lines come from the record you enclosed. Leveri could not tell which one you meant, so it has proposed none — choose the control account.",
        chooseFirst: "Choose the control account first. Nothing is recorded until the work is started.",
    }),
    /**
     * ADR-0019 F — THE BASIS, asked at start when a preparation has a sales
     * ledger: whether sales and debtors are prepared on invoices or on receipts.
     * Not choosing is allowed — Leveri then asks inside the work. Null when the
     * work has nothing to choose.
     */
    basisChoice: z.object({
        question: z.string(),
        hint: z.string(),
        options: z.array(z.object({ value: z.enum(["ACCRUALS", "CASH"]), label: z.string(), detail: z.string() })),
    }).nullable().default(null),
    /** ADR-0021 §5b — the basis the engagement file settles, in words; then there is no choice. */
    basisSettled: z.string().nullable().default(null),
});
/**
 * ADR-0019 F — THE DRAFT BOOKS of a preparation, as the screen reads them.
 * Every figure and word is the server's: the engine's arithmetic, formatted
 * here, never recomputed in the browser.
 */
const StatementLine = z.object({ label: z.string(), text: z.string(), minorUnits: z.number().int() });
const StatementBlock = z.object({ key: z.string(), label: z.string(), lines: z.array(StatementLine), totalText: z.string(), totalMinorUnits: z.number().int() });
export const PreparationView = z.object({
    headline: z.string(),
    verdict: z.enum(["PREPARED", "PREPARED_WITH_QUESTIONS", "BLOCKED_ON_EVIDENCE", "REFUSED"]),
    verdictLabel: z.string(),
    tone: Tone,
    periodText: z.string(),
    periodicityLabel: z.string(),
    basisLabel: z.string(),
    figures: z.array(z.object({ key: z.string(), label: z.string(), text: z.string(), note: z.string().nullable() })),
    trialBalance: z.array(z.object({
        account: z.string(), label: z.string(), groupLabel: z.string(),
        openingText: z.string(), debitText: z.string(), creditText: z.string(), closingText: z.string(), closingMinorUnits: z.number().int(),
    })),
    trialBalanceTotals: z.object({ debitText: z.string(), creditText: z.string(), balanced: z.boolean() }),
    profitAndLoss: z.object({ blocks: z.array(StatementBlock), netProfitText: z.string(), netProfitMinorUnits: z.number().int() }),
    balanceSheet: z.object({ blocks: z.array(StatementBlock), balanced: z.boolean(), assetsText: z.string(), fundingText: z.string() }),
    cashBook: z.array(z.object({ accountLabel: z.string(), classLabel: z.string(), totalText: z.string(), totalMinorUnits: z.number().int(), lines: z.number().int() })),
    mappings: z.array(z.object({ account: z.string(), label: z.string(), classLabel: z.string().nullable(), mappingLabel: z.string() })),
    reviewerItems: z.array(z.string()),
    notes: z.array(z.string()),
    memorySentence: z.string().nullable(),
    /**
     * PREPARE AGAIN WITH MY DECISIONS: how many classifications and mappings a
     * person has decided on the current version, whether the work can be
     * prepared again now (settled work, a viewer who may review, at least one
     * decision), and what to say about it.
     */
    prepareAgain: z.object({ available: z.boolean(), decided: z.number().int(), sentence: z.string().nullable() }),
    /**
     * THE PATTERN GRID (close.account_preparation@2): the bank lines as the
     * patterns an accountant decides on — a channel with its payees beneath it,
     * a payee, or a line nothing groups — month by month, each with how its
     * lines were treated and why. Empty for work prepared by @1.
     */
    grid: z.object({
        months: z.array(z.object({ key: z.string(), label: z.string() })),
        rows: z.array(z.object({
            key: z.string(),
            level: z.enum(["CHANNEL", "PAYEE", "LINE"]),
            /** For a payee beneath a channel row: that row's key. */
            channelKey: z.string().nullable(),
            words: z.string(),
            direction: z.enum(["IN", "OUT"]),
            lineCount: z.number().int(),
            totalText: z.string(),
            totalMinorUnits: z.number().int(),
            /** One cell per grid month, in the grid's order; null where the pattern has no line that month. */
            cells: z.array(z.string().nullable()),
            treatment: z.object({ label: z.string(), state: z.enum(["CLASSIFIED", "OWN_TRANSFER", "ASKED", "MIXED"]), basis: z.string() }),
            /** The classification decision a person reviews for this pattern; null where the pattern has none. */
            decisionId: Uuid.nullable().default(null),
            /**
             * A model's suggested class, kept by Leveri's gate, for a pattern still to
             * classify — a suggestion only: it settles nothing until a person adopts
             * it through the review act. Above materiality it is marked for
             * individual review (BCA-RULING-02).
             */
            suggestion: z.object({ classKey: z.string(), classLabel: z.string(), basis: z.string(), model: z.string(), aboveMateriality: z.boolean() }).nullable().default(null),
        })),
        toClassify: z.number().int(),
        /** Whether a model may be asked for suggestions now: one is configured, and a pattern still to classify has none. */
        suggestionsOffered: z.boolean().default(false),
    }).default({ months: [], rows: [], toClassify: 0, suggestionsOffered: false }),
});
export const WorkFile = WorkSummary.extend({
    instruction: z.string(),
    band: CurrentBand,
    stateLedger: z.array(StateLedgerRow),
    figures: z.array(FigureRow),
    /**
     * THE CURRENT PROFESSIONAL READING, and only it.
     *
     * A resumed piece of work establishes the control balance twice — once in
     * the attempt that reached WAITING and once in the attempt that finished —
     * and listing both makes a page that repeats the same AP balance, the same
     * supplier total and the same difference until a reader cannot tell which
     * one is now. What the CURRENT workpaper version cites is what is current;
     * everything else is history and lives in `superseded`.
     */
    established: z.array(EstablishedItem),
    conclusions: z.array(ConclusionRow),
    /** Earlier readings, kept whole. Provenance, never a second current answer. */
    superseded: z.object({
        established: z.array(EstablishedItem),
        conclusions: z.array(ConclusionRow),
    }).default({ established: [], conclusions: [] }),
    comparability: z.array(ComparabilityRow).default([]),
    /**
     * WHAT THE CAUSE SECTION IS CALLED — decided on the server, like every other
     * word about professional state. "Cause identified" once the engine has
     * established a mechanism; the neutral heading until it has.
     */
    causeHeading: z.string().default(""),
    openRequests: z.array(OpenRequest),
    explanations: z.array(Explanation),
    records: z.array(EnclosedRecord),
    workpapers: z.array(WorkpaperVersion),
    history: z.array(HistoryEntry),
    start: StartReadiness,
    /** Present only once an attempt has run. The engine's own summary sentence. */
    interpretation: z.string().nullable(),
    /**
     * WHAT LEVERI UNDERSTOOD THIS WORK TO BE (L3) — the most recent reading, or
     * null where none was made.
     *
     * It travels ON the file rather than beside it so that a screen showing the
     * work and a screen showing the reading cannot be looking at different
     * moments. Nullable because reading is not compulsory: the work OS was built
     * before this layer existed and still runs without it, which is what keeps a
     * misreading recoverable — the accountant can ignore it entirely and start
     * the work themselves.
     */
    intent: Interpretation.nullable().default(null),
    /**
     * Whether "Read this work" is offered: only on the kind of work the Level-3
     * reader was built for. On any other kind the person has chosen the kind
     * when opening the work, and a reading would contradict them.
     */
    intentOffered: z.boolean().default(true),
    /**
     * THE PLAN, said before it runs: the skill's own procedure in the
     * accountant's words — what it sets out to establish, its steps, and what it
     * will not conclude. Fixed per skill; the chat's plan card shows it for
     * approval before the work starts.
     */
    procedure: z.object({ summary: z.string(), steps: z.array(z.string()), boundaries: z.array(z.string()) }).nullable().default(null),
    /**
     * ADR-0018 — the latest PROPOSED reading of the open exception by a model,
     * or null. It is never a conclusion and changes nothing; it travels on the
     * file so the reading and the work it read are one moment.
     */
    investigation: Investigation.nullable().default(null),
    /** Whether a person may ask for a proposed reading now (configured, and something is open). */
    canInvestigate: z.boolean().default(false),
    /**
     * R6.3 — LEVERI COULD NOT COMPLETE THE TECHNICAL EXECUTION AUTOMATICALLY.
     * Present only while a technical-attention condition is open. It says
     * nothing about the accounting: no Fact, Decision or conclusion changed. A
     * person can try again (`POST /api/work/:id/retry`).
     */
    needsAttention: z.object({
        raisedAt: z.string(),
        raisedAtText: z.string(),
        failedAttempts: z.number().int().positive(),
        sentence: z.string(),
    }).nullable().default(null),
    /**
     * R7 — THE REVIEW of the current workpaper: which version a person would be
     * reviewing, how many of its conclusions a person has signed off (accepted
     * or adjusted), and whether this viewer holds the authority to review.
     * The engine reviews nothing.
     */
    /**
     * R8.1 — THE ENGAGEMENT'S MATERIALITY as it currently stands (a person's
     * judgment, append-only history), formatted on the server. Null when none
     * has been set.
     */
    /** R8.2 — the reconciliation bridge for the CURRENT version; null before any version. */
    bridge: Bridge.nullable().default(null),
    materiality: z.object({
        overallText: z.string(),
        performanceText: z.string(),
        clearlyTrivialText: z.string(),
        basis: z.string(),
        setByName: z.string(),
        setAtText: z.string(),
    }).nullable().default(null),
    review: z.object({
        versionId: Uuid.nullable(),
        versionSeq: z.number().int().positive().nullable(),
        signedOff: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
        viewerMayReview: z.boolean(),
        sentence: z.string(),
        /** ADR-0019 — the optional "what was off" choices, in words, and the acts each may accompany. */
        correctionOptions: z.array(z.object({
            correctionClass: z.enum(["WRONG", "NOISE", "WORDING"]),
            label: z.string(),
            outcomes: z.array(ReviewOutcome),
        })).default([]),
    }).default({ versionId: null, versionSeq: null, signedOff: 0, total: 0, viewerMayReview: false, sentence: "", correctionOptions: [] }),
    /** ADR-0019 F — the draft books, for a preparation; null for a reconciliation. */
    preparation: PreparationView.nullable().default(null),
});
export const WorkFileResponse = z.object({ work: WorkFile });
// ── requests ────────────────────────────────────────────────────────────────
export const CreateWorkRequest = z.object({
    engagementId: Uuid,
    /** What the accountant wants, in their own words. It becomes the objective. */
    instruction: z.string().trim().min(8, "Say a little more about the work — a few words are enough").max(2000),
    /**
     * R9.1 — which kind of work this is (a registered capability key). Absent
     * means the registry's default. An unknown key is refused and creates nothing.
     */
    capability: z.string().trim().regex(/^[a-z][a-z0-9_]{2,63}$/).optional(),
});
export const WorkResponse = z.object({ work: WorkSummary });
/**
 * The record's role travels with the file; the filename is never trusted for
 * it. `roles` carries one per file, in the order the files were sent, for the
 * ordinary case where an accountant attaches several at once and they are not
 * all the same kind. A single `role` still applies to every file.
 */
export const EncloseRecordFields = z.object({ role: RecordRole });
export const EncloseOutcome = z.enum(["ADMITTED_NEW", "RESOLVED_EXISTING"]);
export const EncloseRecordResponse = z.object({
    /** The first record admitted, or null when none was (every file came back to be named). Kept so one-file callers read one record. */
    record: EnclosedRecord.nullable(),
    /** RESOLVED_EXISTING means the same bytes were already admitted — not a duplicate document. Null with `record`. */
    outcome: EncloseOutcome.nullable(),
    /**
     * EVERY record admitted by this act, in the order they were sent. A role
     * holds 0..N records, so enclosing three supplier listings is one act with
     * three results rather than three acts.
     */
    records: z.array(z.object({ record: EnclosedRecord, outcome: EncloseOutcome })).default([]),
    /**
     * The files that could not be read, each with the reader's own reason. They
     * are recorded as refused enclosures — professional history — and the rest
     * of the batch is admitted regardless: one unreadable file among five must
     * not discard the four that were fine.
     */
    refused: z.array(z.object({ filename: z.string(), reason: z.string() })).default([]),
    /**
     * THE SOURCE AGENT could not tell what these files are from their content
     * (a bank statement and a cash book read alike), or they read as nothing
     * this work takes. They are NOT enclosed: each comes back with the kinds it
     * could be, for the person to name.
     */
    undetermined: z.array(z.object({ filename: z.string(), reason: z.string(), candidates: z.array(RecordKind) })).default([]),
    work: WorkFile,
});
/**
 * STARTING THE WORK NAMES A LINE; IT DOES NOT DESCRIBE ONE (L3-CTL-002).
 *
 * This request used to carry the control account's label, the statement label
 * to match on, and the balance side — all taken at face value and fed to the
 * capability. A caller could therefore invent an account that appears on no
 * trial balance, or name a real one and flip its side, and Leveri would
 * reconcile against whatever it was told. The professional SCOPE of the work
 * was defined by the request rather than by the client's records.
 *
 * It now carries one thing: the identity of a candidate the SERVER issued,
 * from the admitted trial balance, for this piece of work. The server
 * re-resolves it and derives the label, the side and everything else from the
 * line it finds. An unknown, foreign or forged identity is refused before a
 * claim, a Plan Version, a Run or a Task exists.
 */
export const StartWorkRequest = z.object({
    control: z.object({ candidateId: z.string().trim().min(16).max(64) }),
    /** ADR-0019 F — the accountant's choice of basis for a preparation; ignored by any other work. */
    basis: z.enum(["ACCRUALS", "CASH"]).optional(),
    /** The caller's own key, so a double-click is one start and not two. */
    submissionKey: z.string().trim().min(8).max(128),
});
/**
 * P4 — READ A WORK'S RECORDS FROM THE CLIENT'S CONNECTED BOOKS. For a control account, which subledger to read
 * when the work's records and words do not already settle it; ignored by any other work.
 */
export const ReadConnectedRecordsRequest = z.object({
    subledger: z.enum(["SUPPLIERS", "CUSTOMERS"]).optional(),
}).strict();
/** "I don't have this": the record a request asks for does not exist, said by a person with an optional note. */
export const DeclareNotAvailableRequest = z.object({
    note: z.string().trim().max(1000).optional(),
    submissionKey: z.string().trim().min(8).max(128),
}).strict();
/** Answering a request with evidence already enclosed. This is the governed resume gate. */
/** R10.2 — a person confirms what the client sent answers this request (and, optionally, others of the same work). */
export const ConfirmClientResponseRequest = z.object({
    responseId: Uuid,
    alsoRequirementIds: z.array(Uuid).max(50).default([]),
    note: z.string().trim().max(2000).optional(),
    submissionKey: z.string().trim().min(8).max(128),
});
export const AnswerRequestRequest = z.object({
    evidenceReferenceIds: z.array(Uuid).min(1, "Cite at least one record"),
    note: z.string().trim().max(2000).optional(),
    submissionKey: z.string().trim().min(8).max(128),
});
/**
 * R7 — REVIEW ONE CONCLUSION, as cited by the workpaper version the person
 * read. The basis is the person's own words (the basis, the reason, or the
 * evidence needed) and is filed with the act. An adjustment states the
 * reviewer's own conclusion from the question's closed set.
 */
export const ReviewConclusionRequest = z.object({
    artifactVersionId: Uuid,
    outcome: ReviewOutcome,
    basis: z.string().trim().min(1, "State the basis for this review").max(2000),
    adjustedConclusion: z.object({
        conclusionKind: z.string().trim().min(1).max(64),
        conclusionText: z.string().trim().min(1, "State the adjusted conclusion").max(2000),
    }).optional(),
    /** ADR-0019: optional — what was off with Leveri's conclusion. */
    correctionClass: z.enum(["WRONG", "NOISE", "WORDING"]).optional(),
    submissionKey: z.string().trim().min(8).max(128),
});
/** R7 — authority, by grant. */
export const AuthorityGrant = z.object({
    id: Uuid,
    memberId: Uuid,
    displayName: z.string(),
    role: z.string(),
    authority: z.enum(["ACCEPT_CONCLUSIONS", "GRANT_AUTHORITY"]),
    scopeKind: z.enum(["FIRM", "ENGAGEMENT"]),
    engagementId: Uuid.nullable(),
    origin: z.enum(["FIRM_FOUNDER", "MEMBER_GRANT", "MIGRATION_BACKFILL"]),
    grantedByName: z.string().nullable(),
    grantedAt: z.string(),
    reason: z.string(),
    revokedAt: z.string().nullable(),
    revokedByName: z.string().nullable(),
    revokeReason: z.string().nullable(),
});
export const AuthorityListResponse = z.object({ grants: z.array(AuthorityGrant) });
/** ADR-0020 (0032) — whether the firm's documents may be read outside Leveri: the latest recorded decision. */
export const DocumentReadingDecision = z.object({
    allowsOutsideReaders: z.boolean(),
    decidedBy: z.object({ memberId: z.string(), displayName: z.string() }).nullable(),
    decidedAt: z.string().nullable(),
    reason: z.string().nullable(),
    authorityGrantId: z.string().nullable(),
});
export const DocumentReadingResponse = z.object({
    documentReading: DocumentReadingDecision,
    /** Whether THIS viewer may decide it (holds GRANT_AUTHORITY) — the server's answer, never inferred from a title. */
    viewerMayDecide: z.boolean(),
    /** The outside readers this deployment has configured, in order (empty: none — nothing can leave Leveri). */
    outsideReaders: z.array(z.string()),
});
export const DecideDocumentReadingRequest = z.object({
    allowsOutsideReaders: z.boolean(),
    reason: z.string().trim().min(1, "State the reason for this decision.").max(2000),
}).strict();
export const GrantAuthorityRequest = z.object({
    memberId: Uuid,
    authority: z.enum(["ACCEPT_CONCLUSIONS", "GRANT_AUTHORITY"]),
    scope: z.object({ kind: z.enum(["FIRM", "ENGAGEMENT"]), engagementId: Uuid.optional() }),
    reason: z.string().trim().min(1, "State the reason for this grant").max(2000),
});
export const RevokeAuthorityRequest = z.object({
    reason: z.string().trim().min(1, "State the reason for revoking").max(2000),
});
export const AuthorityGrantResponse = z.object({ grant: AuthorityGrant });
export const WorkActionResponse = z.object({
    work: WorkFile,
    /** What the act actually did, stated plainly enough to show the accountant. */
    outcome: z.string(),
});
/**
 * THE VOCABULARIES A SCREEN RENDERS, sent rather than mirrored.
 *
 * Served on its own so the words exist before any client or engagement does —
 * the create-an-engagement control needs them on an empty screen.
 */
export const VocabulariesResponse = z.object({
    engagementTypes: z.array(Named),
    engagementStatuses: z.array(Named),
    clientStatuses: z.array(Named),
    /**
     * THE KINDS OF RECORD, before a piece of work exists to ask one about.
     *
     * Records can now be attached ALONGSIDE the instruction that opens the work
     * (R5.1), and the kind is still declared rather than guessed — so the words
     * have to be available on a screen where there is no work yet. Same source
     * as `StartReadiness.kinds`; the browser still holds no copy of them.
     */
    recordKinds: z.array(RecordKind).default([]),
    /**
     * THE KINDS OF WORK Leveri performs, for the composer: what each is called,
     * what it is for in one line, and the kinds of record it takes (so a file
     * can only be declared as something that work uses). `isDefault` marks the
     * kind opened when the person does not choose.
     */
    workKinds: z.array(z.object({ key: z.string(), label: z.string(), purpose: z.string(), roles: z.array(RecordRole), isDefault: z.boolean() })).default([]),
});
// ── R10.1 — asking the client (ADR-0015 §R10) ────────────────────────────────
/** A person at the client who may be asked for evidence. */
export const ClientContact = z.object({
    id: Uuid,
    displayName: z.string(),
    email: z.string().nullable(),
    status: z.enum(["ACTIVE", "INACTIVE"]),
});
export const ClientContactsResponse = z.object({ contacts: z.array(ClientContact) });
export const CreateClientContactRequest = z.object({
    displayName: z.string().trim().min(1, "Give the contact a name").max(200),
    email: z.string().trim().email().max(320).optional(),
});
export const ClientContactResponse = z.object({ contact: ClientContact });
/** A request link as the issuing firm sees it — never its token or hash. */
export const RequestLink = z.object({
    id: Uuid,
    clarificationId: Uuid,
    contactName: z.string(),
    issuedByName: z.string(),
    issuedAt: z.string(),
    expiresAt: z.string(),
    revokedAt: z.string().nullable(),
    lastUsedAt: z.string().nullable(),
    useCount: z.number().int().nonnegative(),
    state: z.enum(["ACTIVE", "EXPIRED", "REVOKED"]),
    /** As printed — decided on the server (the browser law). */
    stateLabel: z.string(),
    expiresAtText: z.string(),
    /** "Open · issued by A. Partner · until 9 Oct 2026 · 1 file received" */
    summaryText: z.string(),
});
export const RequestLinksResponse = z.object({ links: z.array(RequestLink) });
export const RequestLinkResponse = z.object({ link: RequestLink });
export const IssueRequestLinkRequest = z.object({
    clientContactId: Uuid,
    expiresInDays: z.number().int().min(1).max(30).optional(),
});
/**
 * The ONE response that carries the token. `path` puts it in the URL
 * FRAGMENT, which a browser never sends to a server — so no request line,
 * proxy or access log ever holds it.
 */
export const IssueRequestLinkResponse = z.object({
    link: RequestLink,
    token: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    path: z.string(),
    /** The client contact's address the link was emailed to (0043), or null when it was not emailed. */
    emailedTo: z.string().nullable().default(null),
});
/** The public page's only input: the token, in a request BODY (never logged). */
export const PublicRequestTokenRequest = z.object({ token: z.string().max(100) });
export const PublicRequestView = z.object({
    firmName: z.string(),
    contactName: z.string(),
    requirement: z.string(),
    detail: z.string(),
    expiresAt: z.string(),
    expiresAtText: z.string(),
    responses: z.array(z.object({ filename: z.string(), receivedAt: z.string(), receivedAtText: z.string() })),
});
export const PublicRequestResponse = z.object({ request: PublicRequestView });
export const PublicRespondResponse = z.object({ received: z.object({ filename: z.string(), receivedAt: z.string() }) });
// ── R11 — the review workspace (ADR-0017) ────────────────────────────────────
/**
 * Why a work needs someone. A PROJECTION of state earlier rounds own — R11
 * adds no professional state and no act; every row leads to the existing act.
 */
export const WorkspaceQueueKey = z.enum([
    "NEEDS_REVIEW", "REQUIRES_RECONSIDERATION", "CLIENT_RESPONSES", "WAITING_ON_CLIENT", "WAITING_ON_YOU", "EXCEPTIONS", "TECHNICAL_ATTENTION",
]);
/** One reason to act, already worded by the server. */
export const QueueRow = z.object({
    rowKey: z.string(),
    workId: Uuid,
    workTitle: z.string(),
    clientId: Uuid,
    clientName: z.string(),
    engagementTitle: z.string(),
    status: WorkStatus,
    statusLabel: z.string(),
    tone: Tone,
    /** Why it is in this queue, in a sentence. */
    reasonText: z.string(),
    /** A second line where one helps (materiality, the request itself). */
    detailText: z.string().nullable(),
    /** "since 2 October 2026" — a date, never a relative count. */
    sinceText: z.string(),
    /** The person a request waits on (people to chase). */
    contactName: z.string().nullable(),
    actionLabel: z.string(),
    /** Where the act is: the file, at the right section. */
    href: z.string(),
});
export const WorkspaceQueue = z.object({
    key: WorkspaceQueueKey,
    label: z.string(),
    hint: z.string(),
    count: z.number().int().nonnegative(),
    rows: z.array(QueueRow),
    /** Opaque: the complete ordering key of the last row sent. The next page is the rows strictly after it. */
    nextCursor: z.string().nullable(),
});
/** Every work's existing status stays visible, and filters the list of all work. */
export const StatusFilter = z.object({ status: WorkStatus, label: z.string(), count: z.number().int().nonnegative() });
export const WorkspaceResponse = z.object({
    /** Queues with something in them, in priority order; an empty queue never arrives. */
    queues: z.array(WorkspaceQueue),
    /** How many distinct works need someone at all. */
    attentionWorkCount: z.number().int().nonnegative(),
    statusFilters: z.array(StatusFilter),
    allWork: z.object({
        status: WorkStatus.nullable(),
        count: z.number().int().nonnegative(),
        rows: z.array(WorkSummary),
        nextCursor: z.string().nullable(),
    }),
});
export const WorkspaceQueuePage = z.object({ queue: WorkspaceQueue });
export const WorkspaceWorkPage = WorkspaceResponse.shape.allWork;
//# sourceMappingURL=work.js.map